import {
  accessStatusSchema,
  sessionUserSchema,
  userRoleSchema
} from "@dxf-ai-editor/contracts";
import { z } from "zod";

const defaultLocalhostApiBaseUrl = "http://localhost:8080";
const apiBootstrapPath = "/api/v1/bootstrap";
const apiSessionPath = "/api/v1/session";
const apiAdminUsersPath = "/api/v1/admin/users";
const apiWorkspacesPath = "/api/v1/workspaces";
const apiDrawingsPath = "/api/v1/drawings";
const apiAdminAuditPath = "/api/v1/admin/audit";
const apiAdminTelemetrySummaryPath = "/api/v1/admin/telemetry/summary";

const bootstrapConfigSchema = z.object({
  ok: z.literal(true),
  product: z.object({
    name: z.string().min(1),
    stage: z.string().min(1),
    deploymentMode: z.string().min(1),
    serverAlias: z.string().min(1)
  }),
  auth: z.object({
    mode: z.string().min(1),
    providers: z.array(z.string().min(1)),
    gmailOnly: z.boolean()
  }),
  admin: z.object({
    supportedRoles: z.array(userRoleSchema),
    supportedAccessStatuses: z.array(accessStatusSchema),
    bootstrapUsers: z.array(sessionUserSchema)
  }),
  audit: z.object({
    requiredFields: z.array(z.string().min(1))
  })
});

const sessionShellSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    authenticated: z.boolean(),
    currentUser: sessionUserSchema.nullable(),
    authMode: z.string().min(1),
    deploymentMode: z.string().min(1),
    serverAlias: z.string().min(1),
    bootstrapAbsoluteAdminEmail: z.string().email(),
    providers: z.array(z.string().min(1)),
    features: z.object({
      gmailOnlyAuth: z.boolean(),
      adminAccessConsole: z.boolean(),
      auditViewer: z.boolean(),
      history: z.boolean(),
      realtimePresence: z.boolean()
    }),
    rbac: z.object({
      supportedRoles: z.array(userRoleSchema),
      supportedAccessStatuses: z.array(accessStatusSchema)
    })
  })
});

const adminUsersSchema = z.object({
  ok: z.literal(true),
  data: z.array(sessionUserSchema),
  meta: z.object({
    total: z.number().int().nonnegative(),
    bootstrapAbsoluteAdminEmail: z.string().email(),
    blockSelfBootstrapAdmin: z.boolean()
  })
});

const workspaceSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string().min(1),
  slug: z.string().min(1),
  serverAlias: z.string().min(1),
  deploymentMode: z.string().min(1),
  visibility: z.string().min(1),
  ownerEmail: z.string(),
  memberCount: z.number().int().nonnegative()
});

const workspacesSchema = z.object({
  ok: z.literal(true),
  data: z.array(workspaceSchema),
  meta: z.object({
    total: z.number().int().nonnegative(),
    persistent: z.boolean()
  })
});

const drawingSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string().min(1),
  slug: z.string().min(1),
  workspaceSlug: z.string(),
  createdByEmail: z.string(),
  sourceFormat: z.string().min(1),
  sourceKey: z.union([z.string(), z.null()]),
  status: z.string().min(1),
  lastAuditedAt: z.union([z.string(), z.null()])
});

const drawingsSchema = z.object({
  ok: z.literal(true),
  data: z.array(drawingSchema),
  meta: z.object({
    total: z.number().int().nonnegative(),
    persistent: z.boolean()
  })
});

const auditEventSchema = z.object({
  id: z.union([z.string(), z.number()]),
  workspaceSlug: z.string(),
  drawingSlug: z.string(),
  userEmail: z.string(),
  userRole: z.string(),
  command: z.string(),
  alias: z.string(),
  hotkey: z.string().nullable(),
  commandSource: z.string(),
  spaceContext: z.string(),
  entityIds: z.array(z.string()),
  beforeSummary: z.union([z.string(), z.null()]),
  afterSummary: z.union([z.string(), z.null()]),
  status: z.string().min(1),
  occurredAt: z.string().min(1),
  /**
   * CC-1c typed discriminated payload (`{category, op, ...scalars}`),
   * tolerated as an optional passthrough: today's `audit_events` rows do not
   * carry it (the ring→backend flush is CC-1b, gated on
   * API-CONTRACT-CODEGEN), but the admin console's detail panel already
   * pretty-renders it when present so the surface lights up the moment
   * CC-1b lands.
   *
   * TRANSPORT schema accepts ANY JSON member value (`z.unknown()`), not just
   * scalars: a future nested payload member must not fail the whole admin
   * audit query parse (hard error instead of rendering). Scalar narrowing is
   * a PRESENTATION concern — `auditPayloadPresentation.presentAuditPayload`
   * checks each field and routes non-scalar members to the raw-JSON
   * expander.
   */
  params: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional()
});

const adminAuditSchema = z.object({
  ok: z.literal(true),
  data: z.array(auditEventSchema),
  meta: z.object({
    total: z.number().int().nonnegative(),
    persistent: z.boolean(),
    limit: z.number().int().positive(),
    // Optional so the schema still accepts a pre-CC-1d backend during a
    // rolling deploy (the offset echo shipped with the console).
    offset: z.number().int().nonnegative().optional()
  })
});

const telemetryBucketSchema = z.object({
  key: z.string(),
  count: z.number().int().nonnegative()
});

const adminTelemetrySummarySchema = z.object({
  ok: z.literal(true),
  data: z.object({
    total: z.number().int().nonnegative(),
    byCommandType: z.array(telemetryBucketSchema),
    bySource: z.array(telemetryBucketSchema),
    byKind: z.array(telemetryBucketSchema),
    byStatus: z.array(telemetryBucketSchema),
    byDay: z.array(
      z.object({
        day: z.string(),
        count: z.number().int().nonnegative()
      })
    )
  }),
  meta: z.object({
    topLimit: z.number().int().positive(),
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable()
  })
});

export type BootstrapConfig = z.infer<typeof bootstrapConfigSchema>;
export type SessionShell = z.infer<typeof sessionShellSchema>;
export type AdminUsersResponse = z.infer<typeof adminUsersSchema>;
/** @public */
export type WorkspaceRecord = z.infer<typeof workspaceSchema>;
export type WorkspacesResponse = z.infer<typeof workspacesSchema>;
/** @public */
export type DrawingRecord = z.infer<typeof drawingSchema>;
export type DrawingsResponse = z.infer<typeof drawingsSchema>;
/** @public */
export type AuditEventRecord = z.infer<typeof auditEventSchema>;
export type AdminAuditResponse = z.infer<typeof adminAuditSchema>;
export type AdminTelemetrySummaryResponse = z.infer<
  typeof adminTelemetrySummarySchema
>;

/**
 * CC-1d — server-side filter/pagination query for GET /api/v1/admin/audit.
 * Mirrors `AdminAuditIndexController`'s validated params; empty/undefined
 * fields are omitted from the query string.
 */
export type AdminAuditQuery = {
  limit?: number;
  offset?: number;
  user?: string;
  command?: string;
  source?: string;
  space?: string;
  dateFrom?: string;
  dateTo?: string;
};

export type AdminTelemetrySummaryQuery = {
  dateFrom?: string;
  dateTo?: string;
};

export type BackendClient = {
  getBootstrapConfig: () => Promise<BootstrapConfig>;
  getSessionShell: () => Promise<SessionShell>;
  getAdminUsers: () => Promise<AdminUsersResponse>;
  getWorkspaces: () => Promise<WorkspacesResponse>;
  getDrawings: () => Promise<DrawingsResponse>;
  getAdminAudit: (query?: AdminAuditQuery) => Promise<AdminAuditResponse>;
  getAdminTelemetrySummary: (
    query?: AdminTelemetrySummaryQuery
  ) => Promise<AdminTelemetrySummaryResponse>;
};

/**
 * Backend request failure carrying the HTTP status so callers (the admin
 * console's unauthorized/forbidden states) can branch without parsing the
 * message text.
 */
export class BackendRequestError extends Error {
  readonly status: number;

  constructor(url: string, status: number) {
    super(`Backend request failed for ${url} with status ${status}`);
    this.name = "BackendRequestError";
    this.status = status;
  }
}

export type BackendClientOptions = {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  currentOrigin?: string;
};

export type ViteBackendEnv = {
  VITE_API_BASE_URL?: string;
};

export function resolveBackendApiBaseUrl(
  env: ViteBackendEnv | undefined = undefined,
  currentOrigin: string | undefined = globalThis.location?.origin
): string {
  const configuredBaseUrl = env?.VITE_API_BASE_URL?.trim();

  if (!configuredBaseUrl) {
    return resolveLocalDevelopmentApiBaseUrl(currentOrigin) ?? currentOrigin ?? defaultLocalhostApiBaseUrl;
  }

  if (/^https?:\/\//i.test(configuredBaseUrl)) {
    return configuredBaseUrl.replace(/\/+$/u, "");
  }

  if (configuredBaseUrl.startsWith("/")) {
    return configuredBaseUrl.replace(/\/+$/u, "");
  }

  return `/${configuredBaseUrl.replace(/^\/+/u, "").replace(/\/+$/u, "")}`;
}

function resolveLocalDevelopmentApiBaseUrl(currentOrigin: string | undefined): string | null {
  if (!currentOrigin) {
    return null;
  }

  try {
    const parsed = new URL(currentOrigin);

    if (
      (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") &&
      (parsed.port === "5173" || parsed.port === "4173")
    ) {
      return `${parsed.protocol}//${parsed.hostname}:8080`;
    }
  } catch {
    return null;
  }

  return null;
}

export function createBackendClient(options: BackendClientOptions = {}): BackendClient {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const baseUrl = normalizeBaseUrl(
    options.baseUrl ?? resolveBackendApiBaseUrl(import.meta.env as ViteBackendEnv, options.currentOrigin)
  );

  return {
    getBootstrapConfig: () => fetchJson(fetchImpl, buildApiUrl(baseUrl, apiBootstrapPath), bootstrapConfigSchema),
    getSessionShell: () => fetchJson(fetchImpl, buildApiUrl(baseUrl, apiSessionPath), sessionShellSchema),
    getAdminUsers: () => fetchJson(fetchImpl, buildApiUrl(baseUrl, apiAdminUsersPath), adminUsersSchema),
    getWorkspaces: () => fetchJson(fetchImpl, buildApiUrl(baseUrl, apiWorkspacesPath), workspacesSchema),
    getDrawings: () => fetchJson(fetchImpl, buildApiUrl(baseUrl, apiDrawingsPath), drawingsSchema),
    getAdminAudit: (query = {}) =>
      fetchJson(
        fetchImpl,
        buildApiUrlWithQuery(baseUrl, apiAdminAuditPath, {
          limit: query.limit ?? 20,
          ...compactQuery({
            offset: query.offset,
            user: query.user,
            command: query.command,
            source: query.source,
            space: query.space,
            dateFrom: query.dateFrom,
            dateTo: query.dateTo
          })
        }),
        adminAuditSchema
      ),
    getAdminTelemetrySummary: (query = {}) =>
      fetchJson(
        fetchImpl,
        buildApiUrlWithQuery(
          baseUrl,
          apiAdminTelemetrySummaryPath,
          compactQuery({
            dateFrom: query.dateFrom,
            dateTo: query.dateTo
          })
        ),
        adminTelemetrySummarySchema
      )
  };
}

/** Drop undefined / empty-string fields so they never reach the query string. */
function compactQuery(
  query: Record<string, string | number | undefined>
): Record<string, string | number> {
  const compacted: Record<string, string | number> = {};

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") {
      continue;
    }
    compacted[key] = value;
  }

  return compacted;
}

async function fetchJson<T>(
  fetchImpl: typeof fetch,
  url: string,
  schema: z.ZodType<T>
): Promise<T> {
  const response = await fetchImpl(url, {
    method: "GET",
    // Session-cookie convention (matches the telemetry transport in
    // `state/telemetryServerChannel.ts`): dev/preview origins (5173/4173)
    // call the Laravel backend cross-origin at :8080, so session-gated
    // endpoints 401 unless the cookie is sent explicitly.
    credentials: "include",
    headers: {
      accept: "application/json"
    }
  });

  if (!response.ok) {
    throw new BackendRequestError(url, response.status);
  }

  const payload = await response.json();
  return schema.parse(payload);
}

function buildApiUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/u, "")}${path}`;
}

function buildApiUrlWithQuery(
  baseUrl: string,
  path: string,
  query: Record<string, string | number> = {}
): string {
  const url = new URL(buildApiUrl(baseUrl, path), "http://localhost");

  for (const [key, value] of Object.entries(query)) {
    url.searchParams.set(key, String(value));
  }

  return `${baseUrl.replace(/\/+$/u, "")}${path}${url.search ? url.search : ""}`;
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/u, "");
}
